import { useState, useMemo } from 'react';
import { Table, Input, Select, Space, Tag, Typography, Button } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import dayjs from 'dayjs';
import { useStock, useLocations } from '@hooks/useWarehouse';
import type { StockItem, BatchStatus, StockFilters } from '@/types/warehouse';
import ExpiryAlertBadge from './ExpiryAlertBadge';
import BatchDrawer from './BatchDrawer';
import { downloadStockExcel } from '@/api/exportApi';

const { Text } = Typography;

const STATUS_OPTIONS: { value: BatchStatus; label: string }[] = [
  { value: 'Active', label: 'Активна' },
  { value: 'Reserved', label: 'Зарезервовано' },
  { value: 'Blocked', label: 'Заблоковано' },
  { value: 'Recalled', label: 'Відкликано' },
  { value: 'Expired', label: 'Прострочено' },
  { value: 'Quarantine', label: 'Карантин' },
  { value: 'WrittenOff', label: 'Списано' },
];

const EXPIRY_OPTIONS = [
  { value: 30, label: '30 днів' },
  { value: 60, label: '60 днів' },
  { value: 90, label: '90 днів' },
];

const STATUS_COLORS: Record<BatchStatus, string> = {
  Active: 'green',
  Reserved: 'blue',
  Blocked: 'orange',
  Recalled: 'red',
  Expired: 'default',
  Quarantine: 'purple',
  WrittenOff: 'default',
};

const STATUS_LABELS: Record<BatchStatus, string> = {
  Active: 'Активна',
  Reserved: 'Зарезервовано',
  Blocked: 'Заблоковано',
  Recalled: 'Відкликано',
  Expired: 'Прострочено',
  Quarantine: 'Карантин',
  WrittenOff: 'Списано',
};

function getRowClassName(mdRecord: StockItem): string {
  if (mdRecord.status === 'Recalled') return 'row-recalled';
  if (mdRecord.status === 'Blocked') return 'row-blocked';
  if (mdRecord.isExpiringSoon) return 'row-expiring';
  return '';
}

export default function StockTable() {
  const [mdFilters, setMdFilters] = useState<StockFilters>({
    page: 1,
    pageSize: 20,
  });
  const [mdSelectedItem, setMdSelectedItem] = useState<StockItem | null>(null);
  const [mdDrawerOpen, setMdDrawerOpen] = useState(false);
  const [mdExportLoading, setMdExportLoading] = useState(false);

  const mdHandleExportExcel = async () => {
    setMdExportLoading(true);
    try {
      await downloadStockExcel();
    } finally {
      setMdExportLoading(false);
    }
  };

  const { data: mdStockData, isLoading: mdStockLoading } = useStock(mdFilters);
  const { data: mdLocations } = useLocations();

  const mdLocationOptions = useMemo(
    () =>
      (mdLocations ?? []).map((mdLoc) => ({
        value: mdLoc.code,
        label: mdLoc.code + (mdLoc.zone ? ` (${mdLoc.zone})` : ''),
      })),
    [mdLocations]
  );

  const mdColumns: ColumnsType<StockItem> = [
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 120,
      sorter: true,
    },
    {
      title: 'Назва',
      dataIndex: 'productName',
      key: 'productName',
      width: 200,
      ellipsis: true,
    },
    {
      title: 'Партія',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 120,
    },
    {
      title: 'Серійний №',
      dataIndex: 'serialNumber',
      key: 'serialNumber',
      width: 120,
      render: (mdValue: string | null) => mdValue ?? '—',
    },
    {
      title: 'Термін придатності',
      dataIndex: 'expiryDate',
      key: 'expiryDate',
      width: 150,
      render: (mdValue: string | null, mdRecord: StockItem) => (
        <Space>
          <span>{mdValue ? dayjs(mdValue).format('DD.MM.YYYY') : '—'}</span>
          <ExpiryAlertBadge
            mdIsExpiringSoon={mdRecord.isExpiringSoon}
            mdExpiryDate={mdValue}
          />
        </Space>
      ),
    },
    {
      title: 'Локація',
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 100,
      render: (mdValue: string | null) => mdValue ?? '—',
    },
    {
      title: 'Кількість',
      dataIndex: 'quantityOnHand',
      key: 'quantityOnHand',
      width: 90,
      align: 'right',
    },
    {
      title: 'Зарезервовано',
      dataIndex: 'quantityReserved',
      key: 'quantityReserved',
      width: 110,
      align: 'right',
    },
    {
      title: 'Доступно',
      dataIndex: 'quantityAvailable',
      key: 'quantityAvailable',
      width: 90,
      align: 'right',
      render: (mdValue: number) => <Text strong>{mdValue}</Text>,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (mdStatus: BatchStatus) => (
        <Tag color={STATUS_COLORS[mdStatus] ?? 'default'}>{STATUS_LABELS[mdStatus] ?? mdStatus}</Tag>
      ),
    },
  ];

  const handleTableChange = (mdPagination: TablePaginationConfig) => {
    setMdFilters((mdPrev) => ({
      ...mdPrev,
      page: mdPagination.current ?? 1,
      pageSize: mdPagination.pageSize ?? 20,
    }));
  };

  const handleRowClick = (mdRecord: StockItem) => {
    setMdSelectedItem(mdRecord);
    setMdDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setMdDrawerOpen(false);
    setMdSelectedItem(null);
  };

  const handleAdjust = (_mdItem: StockItem) => {
    // This could navigate to adjustment tab or open a modal
    setMdDrawerOpen(false);
  };

  return (
    <>
      <style>{`
        .row-recalled { background-color: #fff1f0 !important; }
        .row-recalled:hover > td { background-color: #ffccc7 !important; }
        .row-blocked { background-color: #fffbe6 !important; }
        .row-blocked:hover > td { background-color: #fff1b8 !important; }
        .row-expiring { background-color: #fff7e6 !important; }
        .row-expiring:hover > td { background-color: #ffe7ba !important; }
      `}</style>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={mdHandleExportExcel}
          loading={mdExportLoading}
        >
          Експорт Excel
        </Button>
      </div>

      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Пошук за SKU, назвою, партією..."
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 300 }}
          onChange={(mdE) => {
            const mdValue = mdE.target.value;
            setMdFilters((mdPrev) => ({ ...mdPrev, search: mdValue || undefined, page: 1 }));
          }}
        />
        <Select
          placeholder="Статус"
          allowClear
          style={{ width: 180 }}
          options={STATUS_OPTIONS}
          onChange={(mdValue) =>
            setMdFilters((mdPrev) => ({ ...mdPrev, status: mdValue, page: 1 }))
          }
        />
        <Select
          placeholder="Термін придатності"
          allowClear
          style={{ width: 160 }}
          options={EXPIRY_OPTIONS}
          onChange={(mdValue) =>
            setMdFilters((mdPrev) => ({ ...mdPrev, expiryDays: mdValue, page: 1 }))
          }
        />
        <Select
          placeholder="Локація"
          allowClear
          style={{ width: 160 }}
          options={mdLocationOptions}
          onChange={(mdValue) =>
            setMdFilters((mdPrev) => ({ ...mdPrev, locationCode: mdValue, page: 1 }))
          }
        />
      </Space>

      <Table
        columns={mdColumns}
        dataSource={mdStockData?.items ?? []}
        rowKey="batchId"
        loading={mdStockLoading}
        rowClassName={getRowClassName}
        pagination={{
          current: mdFilters.page,
          pageSize: mdFilters.pageSize,
          total: mdStockData?.totalCount ?? 0,
          showSizeChanger: true,
          showTotal: (mdTotal) => `Всього: ${mdTotal}`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        onRow={(mdRecord) => ({
          onClick: () => handleRowClick(mdRecord),
          style: { cursor: 'pointer' },
        })}
        scroll={{ x: 1300 }}
        size="middle"
      />

      <BatchDrawer
        mdItem={mdSelectedItem}
        mdOpen={mdDrawerOpen}
        mdOnClose={handleDrawerClose}
        mdOnAdjust={handleAdjust}
      />
    </>
  );
}
